import { apolloClient } from "@App/graphql/client";
import {
  GET_ORGANIZATION_PREMIUM_SERVICES,
  type OrganizationPremiumServicesQuery,
} from "@App/graphql/queries/getOrganizationPremiumServices";
import { useServices } from "@App/hooks/api/useServices";
import { actionCreators, type State } from "@App/state";
import { selectNetworkId } from "@App/state/selectors/orders";
import { changeOnlyManufacturerFilter } from "@App/state/action-creators";
import type { Brand, PremiumService, Service } from "@App/types";
import { Services, SortOrderType } from "@App/types/enums";
import {
  OrganizationIsDeutscheLeasing,
  OrganizationIsMobilityConcept,
} from "@App/utils/conditionals/organization";
import { PremiumServiceIsReplacementOrPickup } from "@App/utils/conditionals/premiumServices";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

export const useHome = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const organization = useSelector((state: State) => state.orders.organization);
  const service = useSelector((state: State) => state.orders.service);
  const place = useSelector((state: State) => state.orders.place);
  const bevSupport = useSelector((state: State) => state.filters.bevSupport);
  const brand = useSelector((state: State) => state.orders.brand);
  const modalDismissed = useSelector((state: State) => state.filters.modalDismissed);
  const premiumServices = useSelector((state: State) => state.orders.premiumServices);
  const [availableSubservices, setAvailableSubservices] = useState<PremiumService[] | any>([]);
  const [availableSubservicesLoading, setAvailableComfortservicesLoading] =
    useState<boolean>(false);
  const [availableComfortservices, setAvailableComfortservices] = useState<PremiumService[] | any>(
    [],
  );
  const [availableComfortservicesLoading, setAvailableSubservicesLoading] =
    useState<boolean>(false);
  const [showSecondaryTireStep, setShowSecondaryTireStep] = useState(false);
  const [tireStepSelection, setTireStepSelection] = useState("");
  const [secondaryTireStepSelection, setSecondaryTireStepSelection] = useState("");
  const commercialVehicleSupport = useSelector(
    (state: State) => state.filters.commercialVehicleSupport,
  );
  const [autoabo, setAutoabo] = useState(false);
  const numberPlate = useSelector((state: State) => state.orders.numberPlate);
  const mileage = useSelector((state: State) => state.orders.mileage);
  const driverID = useSelector((state: State) => state.auth?.driverData?.uuid);
  const networkId = useSelector(selectNetworkId);

  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const { services } = useServices();

  const dispatch = useDispatch();
  const {
    chooseService,
    chooseBrand,
    addPremiumService,
    removePremiumService,
    changeBrandFilter,
    changeBevSupportFilter,
    changeSortByFilter,
    changeCommercialVehicleSupportFilter,
    changeShowAsNonBookable,
    setMessage,
  } = bindActionCreators(actionCreators, dispatch);

  useEffect(() => {
    changeSortByFilter(
      (organization?.settings?.garage_list?.default_sorting as SortOrderType) ??
        SortOrderType.SMALLEST_DISTANCE,
    );
  }, []);

  const selectBrand = (selectedBrand: Brand | null): void => {
    chooseBrand(selectedBrand);
    selectedBrand && setCurrentStep(currentStep + 1);
  };

  const selectEngine = (bev: boolean): void => {
    changeBevSupportFilter(bev);
    if (!bev) {
      chooseBrand(null);
      changeBrandFilter(null);
    }
    setCurrentStep(currentStep + 1);
  };

  const selectCommercialVehicle = (commercialVehicleSupport: boolean): void => {
    changeCommercialVehicleSupportFilter(commercialVehicleSupport);
    setCurrentStep(currentStep + 1);
  };

  const togglePremiumService = (premiumService: PremiumService, premiumServices) => {
    // try to find the service to remove it from the list if already checked
    const exists = premiumServices.some((p) => p.id === premiumService.id);

    if (exists) {
      removePremiumService(premiumService);
      return;
    }

    // add the premium service to the list if nothing found
    addPremiumService(premiumService);
  };

  const togglePremiumServiceCarReplacementOrPickup = (
    premiumService: PremiumService,
    premiumServices,
  ) => {
    // try to find the service to remove it from the list if already checked
    const exists = premiumServices.some((p) => p.id === premiumService.id);

    premiumServices.forEach((premiumService) => {
      if (PremiumServiceIsReplacementOrPickup(premiumService)) {
        removePremiumService(premiumService);
      }
    });

    if (exists) {
      removePremiumService(premiumService);
      return;
    }

    // add the premium service to the list if nothing found
    addPremiumService(premiumService);
  };

  const toggleSingularPremiumService = (premiumService: PremiumService, premiumServices) => {
    // try to find the service to remove it from the list if already checked
    const exists = premiumServices.some((p) => p.id === premiumService.id);

    if (exists) {
      removePremiumService(premiumService);
      return;
    }

    premiumServices.forEach((premiumService) => {
      removePremiumService(premiumService);
    });

    // add the premium service to the list if nothing found
    addPremiumService(premiumService);
  };

  const toggled = (premiumService: PremiumService, premiumServices): boolean => {
    return premiumServices.some((p) => p.id === premiumService.id);
  };
  const serviceToggled = (selectedService: Service): boolean => {
    return selectedService.id === service?.id;
  };

  const nextStep = (): void => {
    setCurrentStep(currentStep + 1);
  };

  const getAvailableSubServices = (serviceId) => {
    setAvailableSubservicesLoading(true);
    apolloClient
      .query<OrganizationPremiumServicesQuery>({
        query: GET_ORGANIZATION_PREMIUM_SERVICES,
        variables: {
          organization: organization.id,
          services: [serviceId],
          driver: driverID ?? null,
          network: networkId,
        },
      })
      .then((res) => {
        const subsServices =
          res.data.organization.premium_services?.filter(
            (premiumService) => premiumService.service_type === "sub-service",
          ) ?? [];

        setAvailableSubservices(subsServices);
        setAvailableSubservicesLoading(false);
      });
  };

  const getAvailableComfortServices = (serviceId) => {
    setAvailableComfortservicesLoading(true);
    apolloClient
      .query<OrganizationPremiumServicesQuery>({
        query: GET_ORGANIZATION_PREMIUM_SERVICES,
        variables: {
          organization: organization.id,
          services: [serviceId],
          driver: driverID ?? null,
          network: networkId,
        },
      })
      .then((res) => {
        const subsServices =
          res.data.organization.premium_services?.filter(
            (premiumService) => premiumService.service_type === "comfort-service",
          ) ?? [];

        setAvailableComfortservices(subsServices);
        setAvailableComfortservicesLoading(false);
      });
  };

  // reload the premium services when the network changes (e.g. after the brand was selected), so the services of the right network are shown
  const lastLoadedNetworkId = useRef(networkId);
  useEffect(() => {
    if (lastLoadedNetworkId.current === networkId) return;
    lastLoadedNetworkId.current = networkId;

    if (!service) return;
    getAvailableSubServices(service.id);
    getAvailableComfortServices(service.id);
  }, [networkId]);

  const selectService = (
    selectedService: Service,
    resetOtherParams: boolean,
    showAsNonBookable: boolean = false,
  ): void => {
    if (selectedService.pivot.external_url) {
      if (OrganizationIsDeutscheLeasing(organization)) {
        window.open(
          selectedService.pivot.external_url + "?" + new URLSearchParams(window.location.search),
          "_self",
        );
      } else {
        window.open(selectedService.pivot.external_url, "_blank");
      }
      return;
    }
    if (selectedService.id === Services.VEHICLEDELIVERY) {
      navigate("fahrzeuglieferung");
      return;
    }

    setAutoabo(selectedService.id === Services.AUTOABO);
    OrganizationIsMobilityConcept(organization) ? changeOnlyManufacturerFilter(false) : "";
    changeShowAsNonBookable(showAsNonBookable);
    if (resetOtherParams) {
      chooseBrand(null);
      changeBrandFilter(null);
      changeBevSupportFilter(false);
      changeCommercialVehicleSupportFilter(false);
    }
    getAvailableSubServices(selectedService.id);
    getAvailableComfortServices(selectedService.id);
    premiumServices?.forEach((premiumService) => {
      removePremiumService(premiumService);
    });
    chooseService(selectedService);
    setCurrentStep(currentStep + 1);

    setSearchParams(
      (prev) => {
        prev.set("service", selectedService.id.toString());
        return prev;
      },
      { replace: true },
    );
  };

  const simpleServiceSelect = (selectedService: Service): void => {
    if (serviceToggled(selectedService)) {
      chooseService(null);
      return;
    }
    setAutoabo(selectedService.id === Services.AUTOABO);
    changeShowAsNonBookable(false);
    chooseService(selectedService);

    setSearchParams(
      (prev) => {
        prev.set("service", selectedService.id.toString());
        return prev;
      },
      { replace: true },
    );
  };

  const selectTireServiceTypeAndService = (
    showAsNonBookable: boolean,
    givenService,
    showSecondaryTireStep: boolean,
    tireStepSelection: string,
    secondaryStep: boolean,
  ): void => {
    setShowSecondaryTireStep(showSecondaryTireStep);
    if (secondaryStep) {
      setSecondaryTireStepSelection(tireStepSelection);
    } else {
      setTireStepSelection(tireStepSelection);
    }
    selectService(
      services.find((service) => service.id === givenService.id),
      false,
      showAsNonBookable,
    );
  };

  const stepUp = () => {
    setCurrentStep(currentStep + 1);
  };

  const stepToOneAndResetOthers = () => {
    chooseBrand(null);
    changeBrandFilter(null);
    changeBevSupportFilter(false);
    changeCommercialVehicleSupportFilter(false);
    setCurrentStep(1);
  };

  const hasAKFContractTypeUrlParam = (urlParams: URLSearchParams): boolean => {
    return urlParams.has("akfcontracttype");
  };

  return {
    currentStep,
    setCurrentStep,
    selectBrand,
    selectEngine,
    selectCommercialVehicle,
    togglePremiumService,
    togglePremiumServiceCarReplacementOrPickup,
    toggled,
    nextStep,
    organization,
    service,
    premiumServices,
    availableSubservices,
    availableComfortservices,
    availableComfortservicesLoading,
    place,
    bevSupport,
    brand,
    modalDismissed,
    setAvailableSubservices,
    availableSubservicesLoading,
    setAvailableSubservicesLoading,
    selectService,
    selectTireServiceTypeAndService,
    secondaryTireStepSelection,
    tireStepSelection,
    showSecondaryTireStep,
    commercialVehicleSupport,
    autoabo,
    stepUp,
    hasAKFContractTypeUrlParam,
    toggleSingularPremiumService,
    stepToOneAndResetOthers,
    numberPlate,
    mileage,
    simpleServiceSelect,
    serviceToggled,
    setMessage,
  };
};
